import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { BRAND, formatApiError } from "@/lib/api";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (user) nav(user.role === "admin" ? "/admin" : "/aluno", { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await login(email, password);
      toast.success(`Bem-vindo(a), ${data.name}!`);
      nav(data.role === "admin" ? "/admin" : "/aluno", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      {/* Left hero */}
      <div className="hidden lg:flex flex-col justify-between p-10 relative overflow-hidden bg-gradient-to-br from-[hsl(218_70%_12%)] via-[hsl(218_60%_10%)] to-[hsl(218_50%_8%)]">
        <div className="absolute inset-0 opacity-30"
             style={{ backgroundImage: "radial-gradient(circle at 20% 80%, hsl(var(--vv-lime)/0.25), transparent 55%), radial-gradient(circle at 80% 20%, hsl(var(--vv-blue)/0.35), transparent 55%)" }} />
        <div className="relative z-10 flex items-center gap-4">
          <img src={BRAND.LOGO} alt="Vôlei Vida" className="h-32 vv-logo-glow" />
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--vv-lime))]">ESCOLA DE VÔLEI</div>
            <h1 className="text-4xl font-black vv-gradient-text leading-none">VÔLEI VIDA</h1>
            <div className="text-muted-foreground">Jardinópolis — 2023</div>
          </div>
        </div>

        <div className="relative z-10 flex items-end justify-center">
          <img src={BRAND.DUO_ACAO} alt="Manguito e Manguita" className="max-h-[520px] w-auto vv-mascot-float" />
        </div>

        <div className="relative z-10 vv-divider-stripe w-40" />
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <Card className="w-full max-w-md vv-card p-8">
          <div className="flex items-center gap-3 lg:hidden mb-6">
            <img src={BRAND.LOGO} alt="Vôlei Vida" className="h-20" />
            <div>
              <h1 className="text-2xl font-black vv-gradient-text leading-none">VÔLEI VIDA</h1>
              <div className="text-xs text-muted-foreground">Jardinópolis</div>
            </div>
          </div>
          <div className="mb-6">
            <div className="text-xs uppercase tracking-widest text-[hsl(var(--vv-lime))]">Acesso ao sistema</div>
            <h2 className="text-3xl font-black mt-1">Entrar</h2>
            <p className="text-sm text-muted-foreground mt-2">
              Use suas credenciais para acessar o painel.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="uppercase text-xs tracking-wider">Email</Label>
              <Input
                id="email" type="email" required autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                data-testid="login-email-input"
                className="mt-2 bg-background/60 h-11"
              />
            </div>
            <div>
              <Label htmlFor="password" className="uppercase text-xs tracking-wider">Senha</Label>
              <Input
                id="password" type="password" required autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                data-testid="login-password-input"
                className="mt-2 bg-background/60 h-11"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-12 font-black uppercase tracking-widest bg-gradient-to-r from-[hsl(var(--vv-blue))] to-[hsl(var(--vv-lime))] text-[hsl(218_50%_10%)] hover:opacity-95"
            >
              {loading ? <Loader2 className="size-4 animate-spin mr-2" /> : null}
              {loading ? "Entrando..." : "Entrar"}
            </Button>
          </form>

          <div className="mt-6 pt-5 border-t border-border text-center text-sm">
            <span className="text-muted-foreground">Ainda não é cadastrado? </span>
            <Link to="/register" className="font-bold text-[hsl(var(--vv-lime))] hover:underline" data-testid="login-go-register">
              Cadastre-se
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
